import Button from "components/Button";
import { ErrorHelper } from "helpers";
import { useEffect, useMemo, useState } from "react";

import { IcoClose } from "assets/icons";
import Loading from "components/Loading";
import { toast } from "react-toastify";
import { ApplicationDocumentService } from "services/applicationDocument";
import {
	AnalysisEventData,
	AnalysisEventDocument,
	AnalysisEventDocumentFolder,
	ApplicationAnalysisData,
	DocumentFolderTypesAndFileExtensions
} from "services/types";
import { AnalysisEventContentLayout, AnalysisEventDocumentDisplay, AnalysisReplyFormContent, EmptyAnalysisContent } from "./modules";
import { AnalysisEventContentTab } from "./modules/AnalysisEventContentLayout";

export enum ContentBoxMode {
	REPLY = "REPLY",
	VIEW = "VIEW"
}

interface AnalysisEventContentProps {
	applicationId: string;
	analysis: ApplicationAnalysisData | null;
	analysisEvent: AnalysisEventData | null;
	contentMode: ContentBoxMode;
	setContentMode: (newMode: ContentBoxMode) => void;
	setSelectedEventId: (newEventId: string | null) => void;
	documentFolderTypesData: DocumentFolderTypesAndFileExtensions | null;
	allowReply?: boolean;
}

export default function AnalysisEventContent({
	applicationId,
	analysis,
	analysisEvent,
	contentMode,
	setContentMode,
	setSelectedEventId,
	documentFolderTypesData,
	allowReply = false
}: Readonly<AnalysisEventContentProps>): JSX.Element {
	const applicationDocumentService = useMemo(ApplicationDocumentService.getInstance, []);
	const [contentSelectedTab, setContentSelectedTab] = useState<AnalysisEventContentTab>(AnalysisEventContentTab.description);
	const [replySelectedTab, setReplySelectedTab] = useState<AnalysisEventContentTab>(AnalysisEventContentTab.description);
	const isEmpty = analysis === null || analysisEvent === null;
	const [eventDocuments, setEventDocuments] = useState<AnalysisEventDocument[] | null>(null);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(eventDocuments === null);
	const [eventTollStations, setEventTollStations] = useState<any[]>([]);

	useEffect(() => {
		if (!isEmpty) {
			setIsLoadingDocuments(true);
			applicationDocumentService
				.listAnalysisEventDocuments(applicationId, analysisEvent.id)
				.then((data) => setEventDocuments(data))
				.catch((err) => {
					toast.error(ErrorHelper.getResponseErrorMessage(err));
				})
				.finally(() => setIsLoadingDocuments(false));
		}
	}, [analysis, analysisEvent]);

	const { populatedDocumentFolders, assortedDocuments } = useMemo(() => {
		const newAssortedDocuments = eventDocuments?.filter((document) => !document.exemptionDocumentFolderType) ?? [];
		const folderDocuments = eventDocuments?.filter((document) => !!document.exemptionDocumentFolderType) ?? [];
		const newDocumentFolders =
			documentFolderTypesData?.documentFolderTypes
				.map((folderType): AnalysisEventDocumentFolder => {
					return {
						folderTypeData: folderType,
						exemptionDocumentFolderType: folderType.id,
						documents: folderDocuments.filter((doc) => doc.exemptionDocumentFolderType === folderType.id)
					};
				})
				.filter((docFolder) => docFolder.documents.length > 0) ?? [];
		return {
			populatedDocumentFolders: newDocumentFolders,
			assortedDocuments: newAssortedDocuments
		};
	}, [documentFolderTypesData, eventDocuments]);

	useEffect(() => {
		if (!analysisEvent?.tollIsentos?.length) return;

		setEventTollStations(analysisEvent.tollIsentos);
	}, [analysisEvent]);

	const documentsCount = assortedDocuments.length + populatedDocumentFolders.length;
	const displayedTabs: AnalysisEventContentTab[] = [
		AnalysisEventContentTab.description,
		documentsCount > 0 ? AnalysisEventContentTab.attachments : undefined
	].filter((i) => !!i) as AnalysisEventContentTab[];

	const fullDescription = useMemo(() => {
		if (!analysisEvent) return "";

		return analysisEvent.description;
	}, [analysisEvent]);

	if (isEmpty) return <EmptyAnalysisContent />;

	if (isLoadingDocuments)
		return (
			<div className="flex w-full h-full items-center justify-center">
				<Loading size={48} />
			</div>
		);

	if (contentMode === ContentBoxMode.REPLY) {
		return (
			<AnalysisReplyFormContent
				key={`${applicationId}-${contentMode}`}
				selectedTab={replySelectedTab}
				setSelectedTab={setReplySelectedTab}
				applicationId={applicationId}
				analysisEvent={analysisEvent}
				setContentMode={setContentMode}
				documentFolderTypesData={documentFolderTypesData}
			/>
		);
	}

	return (
		<AnalysisEventContentLayout
			key={`${applicationId}-${contentMode}`}
			displayedTabs={displayedTabs}
			selectedTab={contentSelectedTab}
			setSelectedTab={setContentSelectedTab}
			header={
				<div className="flex flex-row w-full min-h-14 h-fit items-center justify-between">
					<h2 className="font-semibold text-base md:text-xl overflow-wrap--anywhere">{analysisEvent.title}</h2>
					<Button
						hierarchy="ghost"
						size="small"
						kind="icon"
						className="bg-transparent hover:bg-neutral-high-200 md:hidden flex inline-block w-10"
						onClick={() => setSelectedEventId(null)}
					>
						<IcoClose />
					</Button>
				</div>
			}
			description={
				<div className="w-full h-full border md:border-0 border-neutral-high-100 rounded p-2 md:text-base text-sm whitespace-pre-wrap">
					{fullDescription}
					{eventTollStations.length > 0 && (
						<div className="pt-4">
							<div className="font-semibold">Praças isentas:</div>
							<ul className="list-disc pl-6 pt-2">
								{eventTollStations.map((tollStation) => (
									<li key={tollStation.id}>{tollStation.name}</li>
								))}
							</ul>
						</div>
					)}
				</div>
			}
			documents={
				!!documentsCount &&
				documentFolderTypesData && (
					<AnalysisEventDocumentDisplay
						assortedDocuments={assortedDocuments}
						documentFolders={populatedDocumentFolders}
						fileExtensions={documentFolderTypesData.fileExtensions}
					/>
				)
			}
			footer={
				allowReply && (
					<Button
						onClick={() => {
							setContentMode(ContentBoxMode.REPLY);
							setReplySelectedTab(AnalysisEventContentTab.description);
						}}
						className="w-full md:w-fit"
					>
						Responder
					</Button>
				)
			}
		/>
	);
}
